(function(root){
  root.OpenSeadragon = root.OpenSeadragon || {};
  // Full inlined worker source (libs + core)
  root.OpenSeadragon.__FABRIC_WORKER_SOURCE__ = `
/**
 * Worker-side half of the FlexDrawer HTTP bridge.
 *
 * This shim is concatenated into every FlexDrawer worker blob. It exposes
 * \`self.requestFetch(url, init)\` for the worker's network call sites to use in
 * place of \`fetch(...)\` when the main thread has wired an HttpAdapter via
 * installHttpBridge() (see http-bridge.main.js).
 *
 * Routing is gated by \`self.__hasHttpBridge\`: it starts false and flips to true
 * only after the main side posts \`{ type: 'http:bridge-on' }\`. When the flag is
 * false, call sites must fall back to native fetch — so worker code stays
 * usable standalone (no adapter wired) without any behavioral change.
 *
 * Protocol (worker ↔ main):
 *   worker → main: { type: 'http:request', id, url, init }
 *   main → worker: { type: 'http:response', id, status, ok, headers, body }
 *                 ({ body } is a transferable ArrayBuffer; zero-copy)
 *   main → worker: { type: 'http:error', id, message, name }
 *   worker → main: { type: 'http:cancel', id }
 *
 * Response objects returned from \`requestFetch\` expose status / ok / headers
 * plus async arrayBuffer() / json() / text() — the same surface the existing
 * worker call sites already use on real \`Response\` instances.
 */
(function attachHttpBridge(scope) {
    if (scope.requestFetch) {
        // already attached — ignore double inclusion
        return;
    }

    scope.__hasHttpBridge = false;

    var nextId = 1;
    var pending = new Map(); // id -> { resolve, reject, signal, abortListener }

    scope.addEventListener('message', function (event) {
        var msg = event.data;
        if (!msg || typeof msg.type !== 'string' || msg.type.indexOf('http:') !== 0) {
            return;
        }

        if (msg.type === 'http:bridge-on') {
            scope.__hasHttpBridge = true;
            return;
        }

        if (msg.type === 'http:bridge-off') {
            scope.__hasHttpBridge = false;
            return;
        }

        var waiter = pending.get(msg.id);
        if (!waiter) {
            return;
        }

        pending.delete(msg.id);
        detachAbortListener(waiter);

        if (msg.type === 'http:response') {
            waiter.resolve(makeResponseShim(msg));
        } else if (msg.type === 'http:error') {
            var err = new Error(msg.message || 'flex-renderer http bridge: request failed');
            if (msg.name) {
                err.name = msg.name;
            }
            waiter.reject(err);
        }
    });

    scope.requestFetch = function requestFetch(url, init) {
        var id = nextId++;
        var safeInit;
        try {
            safeInit = serializeInit(init);
        } catch (err) {
            return Promise.reject(err);
        }

        var signal = init && init.signal;
        if (signal && signal.aborted) {
            return Promise.reject(makeAbortError(signal.reason));
        }

        return new Promise(function (resolve, reject) {
            var waiter = {
                resolve: resolve,
                reject: reject,
                signal: signal || null,
                abortListener: null
            };
            pending.set(id, waiter);

            try {
                scope.postMessage({
                    type: 'http:request',
                    id: id,
                    url: url,
                    init: safeInit
                });
            } catch (err) {
                pending.delete(id);
                reject(err);
                return;
            }

            if (signal) {
                waiter.abortListener = function () {
                    if (!pending.has(id)) {
                        return;
                    }
                    pending.delete(id);
                    try {
                        scope.postMessage({ type: 'http:cancel', id: id });
                    } catch (_) { /* ignore */ }
                    reject(makeAbortError(signal.reason));
                };
                signal.addEventListener('abort', waiter.abortListener, { once: true });
            }
        });
    };

    function detachAbortListener(waiter) {
        if (waiter.signal && waiter.abortListener) {
            try {
                waiter.signal.removeEventListener('abort', waiter.abortListener);
            } catch (_) { /* ignore */ }
            waiter.abortListener = null;
        }
    }

    function makeResponseShim(message) {
        var status = message.status;
        var ok = message.ok;
        var headers = message.headers || {};
        var body = message.body;

        return {
            status: status,
            ok: ok,
            headers: headers,
            arrayBuffer: function () { return Promise.resolve(body); },
            json: function () {
                try {
                    return Promise.resolve(JSON.parse(decodeBody(body)));
                } catch (err) {
                    return Promise.reject(err);
                }
            },
            text: function () {
                return Promise.resolve(decodeBody(body));
            }
        };
    }

    function decodeBody(body) {
        if (!body) {
            return '';
        }
        // body is always an ArrayBuffer when present
        return new TextDecoder('utf-8').decode(new Uint8Array(body));
    }

    function makeAbortError(reason) {
        var message = typeof reason === 'string' ? reason : (reason && reason.message) || 'aborted';
        // DOMException is available in workers in all targeted runtimes
        try {
            return new DOMException(message, 'AbortError');
        } catch (_) {
            var err = new Error(message);
            err.name = 'AbortError';
            return err;
        }
    }

    function serializeInit(init) {
        var out = {};
        if (!init || typeof init !== 'object') {
            return out;
        }

        if (init.method) {
            out.method = String(init.method);
        }

        if (init.headers) {
            out.headers = flattenHeaders(init.headers);
        }

        if (init.body !== undefined && init.body !== null) {
            var body = init.body;
            if (typeof body === 'string' || body instanceof ArrayBuffer) {
                out.body = body;
            } else if (ArrayBuffer.isView && ArrayBuffer.isView(body)) {
                out.body = body;
            } else {
                throw new TypeError('flex-renderer http bridge: unsupported request body type');
            }
        }

        if (init.credentials) {
            out.credentials = init.credentials;
        }
        if (init.cache) {
            out.cache = init.cache;
        }

        return out;
    }

    function flattenHeaders(headers) {
        var out = {};
        if (!headers) {
            return out;
        }
        if (typeof headers.forEach === 'function' && !Array.isArray(headers)) {
            // Headers instance or Map-like
            headers.forEach(function (value, key) {
                out[String(key).toLowerCase()] = String(value);
            });
            return out;
        }
        if (Array.isArray(headers)) {
            for (var i = 0; i < headers.length; i++) {
                var entry = headers[i];
                if (Array.isArray(entry) && entry.length >= 2) {
                    out[String(entry[0]).toLowerCase()] = String(entry[1]);
                }
            }
            return out;
        }
        if (typeof headers === 'object') {
            for (var key in headers) {
                if (Object.prototype.hasOwnProperty.call(headers, key)) {
                    out[key.toLowerCase()] = String(headers[key]);
                }
            }
        }
        return out;
    }
})(self);

(function(){function r(e,n,t){function o(i,f){if(!n[i]){if(!e[i]){var c="function"==typeof require&&require;if(!f&&c)return c(i,!0);if(u)return u(i,!0);var a=new Error("Cannot find module '"+i+"'");throw a.code="MODULE_NOT_FOUND",a}var p=n[i]={exports:{}};e[i][0].call(p.exports,function(r){var n=e[i][1][r];return o(n||r)},p,p.exports,r,e,n,t)}return n[i].exports}for(var u="function"==typeof require&&require,i=0;i<t.length;i++)o(t[i]);return o}return r})()({1:[function(require,module,exports){"use strict";var _earcut=_interopRequireDefault(require("earcut"));function _interopRequireDefault(e){return e&&e.__esModule?e:{default:e}}self.earcut=_earcut.default},{earcut:2}],2:[function(require,module,exports){"use strict";Object.defineProperty(exports,"__esModule",{value:true});exports.default=earcut;exports.deviation=deviation;exports.flatten=flatten;function earcut(data,holeIndices,dim=2){const hasHoles=holeIndices&&holeIndices.length;const outerLen=hasHoles?holeIndices[0]*dim:data.length;let outerNode=linkedList(data,0,outerLen,dim,true);const triangles=[];if(!outerNode||outerNode.next===outerNode.prev)return triangles;let minX,minY,invSize;if(hasHoles)outerNode=eliminateHoles(data,holeIndices,outerNode,dim);if(data.length>80*dim){minX=data[0];minY=data[1];let maxX=minX;let maxY=minY;for(let i=dim;i<outerLen;i+=dim){const x=data[i];const y=data[i+1];if(x<minX)minX=x;if(y<minY)minY=y;if(x>maxX)maxX=x;if(y>maxY)maxY=y}invSize=Math.max(maxX-minX,maxY-minY);invSize=invSize!==0?32767/invSize:0}earcutLinked(outerNode,triangles,dim,minX,minY,invSize,0);return triangles}function linkedList(data,start,end,dim,clockwise){let last;if(clockwise===signedArea(data,start,end,dim)>0){for(let i=start;i<end;i+=dim)last=insertNode(i/dim|0,data[i],data[i+1],last)}else{for(let i=end-dim;i>=start;i-=dim)last=insertNode(i/dim|0,data[i],data[i+1],last)}if(last&&equals(last,last.next)){removeNode(last);last=last.next}return last}function filterPoints(start,end){if(!start)return start;if(!end)end=start;let p=start,again;do{again=false;if(!p.steiner&&(equals(p,p.next)||area(p.prev,p,p.next)===0)){removeNode(p);p=end=p.prev;if(p===p.next)break;again=true}else{p=p.next}}while(again||p!==end);return end}function earcutLinked(ear,triangles,dim,minX,minY,invSize,pass){if(!ear)return;if(!pass&&invSize)indexCurve(ear,minX,minY,invSize);let stop=ear;while(ear.prev!==ear.next){const prev=ear.prev;const next=ear.next;if(invSize?isEarHashed(ear,minX,minY,invSize):isEar(ear)){triangles.push(prev.i,ear.i,next.i);removeNode(ear);ear=next.next;stop=next.next;continue}ear=next;if(ear===stop){if(!pass){earcutLinked(filterPoints(ear),triangles,dim,minX,minY,invSize,1)}else if(pass===1){ear=cureLocalIntersections(filterPoints(ear),triangles);earcutLinked(ear,triangles,dim,minX,minY,invSize,2)}else if(pass===2){splitEarcut(ear,triangles,dim,minX,minY,invSize)}break}}}function isEar(ear){const a=ear.prev,b=ear,c=ear.next;if(area(a,b,c)>=0)return false;const ax=a.x,bx=b.x,cx=c.x,ay=a.y,by=b.y,cy=c.y;const x0=Math.min(ax,bx,cx),y0=Math.min(ay,by,cy),x1=Math.max(ax,bx,cx),y1=Math.max(ay,by,cy);let p=c.next;while(p!==a){if(p.x>=x0&&p.x<=x1&&p.y>=y0&&p.y<=y1&&pointInTriangleExceptFirst(ax,ay,bx,by,cx,cy,p.x,p.y)&&area(p.prev,p,p.next)>=0)return false;p=p.next}return true}function isEarHashed(ear,minX,minY,invSize){const a=ear.prev,b=ear,c=ear.next;if(area(a,b,c)>=0)return false;const ax=a.x,bx=b.x,cx=c.x,ay=a.y,by=b.y,cy=c.y;const x0=Math.min(ax,bx,cx),y0=Math.min(ay,by,cy),x1=Math.max(ax,bx,cx),y1=Math.max(ay,by,cy);const minZ=zOrder(x0,y0,minX,minY,invSize),maxZ=zOrder(x1,y1,minX,minY,invSize);let p=ear.prevZ,n=ear.nextZ;while(p&&p.z>=minZ&&n&&n.z<=maxZ){if(p.x>=x0&&p.x<=x1&&p.y>=y0&&p.y<=y1&&p!==a&&p!==c&&pointInTriangleExceptFirst(ax,ay,bx,by,cx,cy,p.x,p.y)&&area(p.prev,p,p.next)>=0)return false;p=p.prevZ;if(n.x>=x0&&n.x<=x1&&n.y>=y0&&n.y<=y1&&n!==a&&n!==c&&pointInTriangleExceptFirst(ax,ay,bx,by,cx,cy,n.x,n.y)&&area(n.prev,n,n.next)>=0)return false;n=n.nextZ}while(p&&p.z>=minZ){if(p.x>=x0&&p.x<=x1&&p.y>=y0&&p.y<=y1&&p!==a&&p!==c&&pointInTriangleExceptFirst(ax,ay,bx,by,cx,cy,p.x,p.y)&&area(p.prev,p,p.next)>=0)return false;p=p.prevZ}while(n&&n.z<=maxZ){if(n.x>=x0&&n.x<=x1&&n.y>=y0&&n.y<=y1&&n!==a&&n!==c&&pointInTriangleExceptFirst(ax,ay,bx,by,cx,cy,n.x,n.y)&&area(n.prev,n,n.next)>=0)return false;n=n.nextZ}return true}function cureLocalIntersections(start,triangles){let p=start;do{const a=p.prev,b=p.next.next;if(!equals(a,b)&&intersects(a,p,p.next,b)&&locallyInside(a,b)&&locallyInside(b,a)){triangles.push(a.i,p.i,b.i);removeNode(p);removeNode(p.next);p=start=b}p=p.next}while(p!==start);return filterPoints(p)}function splitEarcut(start,triangles,dim,minX,minY,invSize){let a=start;do{let b=a.next.next;while(b!==a.prev){if(a.i!==b.i&&isValidDiagonal(a,b)){let c=splitPolygon(a,b);a=filterPoints(a,a.next);c=filterPoints(c,c.next);earcutLinked(a,triangles,dim,minX,minY,invSize,0);earcutLinked(c,triangles,dim,minX,minY,invSize,0);return}b=b.next}a=a.next}while(a!==start)}function eliminateHoles(data,holeIndices,outerNode,dim){const queue=[];for(let i=0,len=holeIndices.length;i<len;i++){const start=holeIndices[i]*dim;const end=i<len-1?holeIndices[i+1]*dim:data.length;const list=linkedList(data,start,end,dim,false);if(list===list.next)list.steiner=true;queue.push(getLeftmost(list))}queue.sort(compareXYSlope);for(let i=0;i<queue.length;i++){outerNode=eliminateHole(queue[i],outerNode)}return outerNode}function compareXYSlope(a,b){let result=a.x-b.x;if(result===0){result=a.y-b.y;if(result===0){const aSlope=(a.next.y-a.y)/(a.next.x-a.x);const bSlope=(b.next.y-b.y)/(b.next.x-b.x);result=aSlope-bSlope}}return result}function eliminateHole(hole,outerNode){const bridge=findHoleBridge(hole,outerNode);if(!bridge){return outerNode}const bridgeReverse=splitPolygon(bridge,hole);filterPoints(bridgeReverse,bridgeReverse.next);return filterPoints(bridge,bridge.next)}function findHoleBridge(hole,outerNode){let p=outerNode;const hx=hole.x;const hy=hole.y;let qx=-Infinity;let m;if(equals(hole,p))return p;do{if(equals(hole,p.next))return p.next;else if(hy<=p.y&&hy>=p.next.y&&p.next.y!==p.y){const x=p.x+(hy-p.y)*(p.next.x-p.x)/(p.next.y-p.y);if(x<=hx&&x>qx){qx=x;m=p.x<p.next.x?p:p.next;if(x===hx)return m}}p=p.next}while(p!==outerNode);if(!m)return null;const stop=m;const mx=m.x;const my=m.y;let tanMin=Infinity;p=m;do{if(hx>=p.x&&p.x>=mx&&hx!==p.x&&pointInTriangle(hy<my?hx:qx,hy,mx,my,hy<my?qx:hx,hy,p.x,p.y)){const tan=Math.abs(hy-p.y)/(hx-p.x);if(locallyInside(p,hole)&&(tan<tanMin||tan===tanMin&&(p.x>m.x||p.x===m.x&&sectorContainsSector(m,p)))){m=p;tanMin=tan}}p=p.next}while(p!==stop);return m}function sectorContainsSector(m,p){return area(m.prev,m,p.prev)<0&&area(p.next,m,m.next)<0}function indexCurve(start,minX,minY,invSize){let p=start;do{if(p.z===0)p.z=zOrder(p.x,p.y,minX,minY,invSize);p.prevZ=p.prev;p.nextZ=p.next;p=p.next}while(p!==start);p.prevZ.nextZ=null;p.prevZ=null;sortLinked(p)}function sortLinked(list){let numMerges;let inSize=1;do{let p=list;let e;list=null;let tail=null;numMerges=0;while(p){numMerges++;let q=p;let pSize=0;for(let i=0;i<inSize;i++){pSize++;q=q.nextZ;if(!q)break}let qSize=inSize;while(pSize>0||qSize>0&&q){if(pSize!==0&&(qSize===0||!q||p.z<=q.z)){e=p;p=p.nextZ;pSize--}else{e=q;q=q.nextZ;qSize--}if(tail)tail.nextZ=e;else list=e;e.prevZ=tail;tail=e}p=q}tail.nextZ=null;inSize*=2}while(numMerges>1);return list}function zOrder(x,y,minX,minY,invSize){x=(x-minX)*invSize|0;y=(y-minY)*invSize|0;x=(x|x<<8)&16711935;x=(x|x<<4)&252645135;x=(x|x<<2)&858993459;x=(x|x<<1)&1431655765;y=(y|y<<8)&16711935;y=(y|y<<4)&252645135;y=(y|y<<2)&858993459;y=(y|y<<1)&1431655765;return x|y<<1}function getLeftmost(start){let p=start,leftmost=start;do{if(p.x<leftmost.x||p.x===leftmost.x&&p.y<leftmost.y)leftmost=p;p=p.next}while(p!==start);return leftmost}function pointInTriangle(ax,ay,bx,by,cx,cy,px,py){return(cx-px)*(ay-py)>=(ax-px)*(cy-py)&&(ax-px)*(by-py)>=(bx-px)*(ay-py)&&(bx-px)*(cy-py)>=(cx-px)*(by-py)}function pointInTriangleExceptFirst(ax,ay,bx,by,cx,cy,px,py){return!(ax===px&&ay===py)&&pointInTriangle(ax,ay,bx,by,cx,cy,px,py)}function isValidDiagonal(a,b){return a.next.i!==b.i&&a.prev.i!==b.i&&!intersectsPolygon(a,b)&&(locallyInside(a,b)&&locallyInside(b,a)&&middleInside(a,b)&&(area(a.prev,a,b.prev)||area(a,b.prev,b))||equals(a,b)&&area(a.prev,a,a.next)>0&&area(b.prev,b,b.next)>0)}function area(p,q,r){return(q.y-p.y)*(r.x-q.x)-(q.x-p.x)*(r.y-q.y)}function equals(p1,p2){return p1.x===p2.x&&p1.y===p2.y}function intersects(p1,q1,p2,q2){const o1=sign(area(p1,q1,p2));const o2=sign(area(p1,q1,q2));const o3=sign(area(p2,q2,p1));const o4=sign(area(p2,q2,q1));if(o1!==o2&&o3!==o4)return true;if(o1===0&&onSegment(p1,p2,q1))return true;if(o2===0&&onSegment(p1,q2,q1))return true;if(o3===0&&onSegment(p2,p1,q2))return true;if(o4===0&&onSegment(p2,q1,q2))return true;return false}function onSegment(p,q,r){return q.x<=Math.max(p.x,r.x)&&q.x>=Math.min(p.x,r.x)&&q.y<=Math.max(p.y,r.y)&&q.y>=Math.min(p.y,r.y)}function sign(num){return num>0?1:num<0?-1:0}function intersectsPolygon(a,b){let p=a;do{if(p.i!==a.i&&p.next.i!==a.i&&p.i!==b.i&&p.next.i!==b.i&&intersects(p,p.next,a,b))return true;p=p.next}while(p!==a);return false}function locallyInside(a,b){return area(a.prev,a,a.next)<0?area(a,b,a.next)>=0&&area(a,a.prev,b)>=0:area(a,b,a.prev)<0||area(a,a.next,b)<0}function middleInside(a,b){let p=a;let inside=false;const px=(a.x+b.x)/2;const py=(a.y+b.y)/2;do{if(p.y>py!==p.next.y>py&&p.next.y!==p.y&&px<(p.next.x-p.x)*(py-p.y)/(p.next.y-p.y)+p.x)inside=!inside;p=p.next}while(p!==a);return inside}function splitPolygon(a,b){const a2=createNode(a.i,a.x,a.y),b2=createNode(b.i,b.x,b.y),an=a.next,bp=b.prev;a.next=b;b.prev=a;a2.next=an;an.prev=a2;b2.next=a2;a2.prev=b2;bp.next=b2;b2.prev=bp;return b2}function insertNode(i,x,y,last){const p=createNode(i,x,y);if(!last){p.prev=p;p.next=p}else{p.next=last.next;p.prev=last;last.next.prev=p;last.next=p}return p}function removeNode(p){p.next.prev=p.prev;p.prev.next=p.next;if(p.prevZ)p.prevZ.nextZ=p.nextZ;if(p.nextZ)p.nextZ.prevZ=p.prevZ}function createNode(i,x,y){return{i:i,x:x,y:y,prev:null,next:null,z:0,prevZ:null,nextZ:null,steiner:false}}function deviation(data,holeIndices,dim,triangles){const hasHoles=holeIndices&&holeIndices.length;const outerLen=hasHoles?holeIndices[0]*dim:data.length;let polygonArea=Math.abs(signedArea(data,0,outerLen,dim));if(hasHoles){for(let i=0,len=holeIndices.length;i<len;i++){const start=holeIndices[i]*dim;const end=i<len-1?holeIndices[i+1]*dim:data.length;polygonArea-=Math.abs(signedArea(data,start,end,dim))}}let trianglesArea=0;for(let i=0;i<triangles.length;i+=3){const a=triangles[i]*dim;const b=triangles[i+1]*dim;const c=triangles[i+2]*dim;trianglesArea+=Math.abs((data[a]-data[c])*(data[b+1]-data[a+1])-(data[a]-data[b])*(data[c+1]-data[a+1]))}return polygonArea===0&&trianglesArea===0?0:Math.abs((trianglesArea-polygonArea)/polygonArea)}function signedArea(data,start,end,dim){let sum=0;for(let i=start,j=end-dim;i<end;i+=dim){sum+=(data[j]-data[i])*(data[i+1]+data[j+1]);j=i}return sum}function flatten(data){const vertices=[];const holes=[];const dimensions=data[0][0].length;let holeIndex=0;let prevLen=0;for(const ring of data){for(const p of ring){for(let d=0;d<dimensions;d++)vertices.push(p[d])}if(prevLen){holeIndex+=prevLen;holes.push(holeIndex)}prevLen=ring.length}return{vertices:vertices,holes:holes,dimensions:dimensions}}},{}]},{},[1]);
// fabric-geom.worker.js  (single-rectangular-tile, unit-normalized)
/* global self */

let CONFIG = {
    width: 0,
    height: 0,
    minLevel: 0,
    maxLevel: 0,
    origin: { x: 0, y: 0 }
};

// id -> { aabb:{x,y,w,h}, meshes:{fills,lines} } ; meshes are in IMAGE space
const OBJECTS = new Map();

// Messages:
//  - { type: 'config', width, height, origin? }
//  - { type: 'addOrUpdate', id, fabric, style }
//  - { type: 'remove', id }
//  - { type: 'tiles', z, keys:[ 'z/x/y', ... ] }  -> returns same unit batch for all keys (single-tile mode)

self.onmessage = (e) => {
    const m = e.data || {};

    try {
        if (m.type === 'config') {
            if (typeof m.width === 'number') {
                CONFIG.width = m.width;
            }
            if (typeof m.height === 'number') {
                CONFIG.height = m.height;
            }
            if (m.origin) {
                CONFIG.origin = m.origin;
            }

            CONFIG.minLevel = 0;
            CONFIG.maxLevel = 0;

            self.postMessage({ type: 'config', ok: true });
            return;
        }

        if (m.type === 'addOrUpdate') {
            const id = m.id;
            const fabric = m.fabric;
            const style = m.style;

            const aabb = computeAABB(fabric);
            const meshes = toMeshes(fabric, style);

            OBJECTS.set(id, { aabb: aabb, meshes: meshes });

            self.postMessage({ type: 'ack', id: id, ok: true });
            return;
        }

        if (m.type === 'remove') {
            const id = m.id;

            OBJECTS.delete(id);

            self.postMessage({ type: 'ack', ok: true });
            return;
        }

        if (m.type === 'tiles') {
            const unit = buildUnitBatchesFromAllObjects();

            const pack = (b) => {
                if (!b) {
                    return undefined;
                }
                return {
                    vertices: b.positions.buffer,
                    colors: b.colors.buffer,
                    indices: b.indices.buffer
                };
            };

            const rec = {
                fills: pack(unit.fills),
                lines: pack(unit.lines)
            };

            const transfers = [];
            if (rec.fills) {
                transfers.push(rec.fills.vertices);
                transfers.push(rec.fills.colors);
                transfers.push(rec.fills.indices);
            }
            if (rec.lines) {
                transfers.push(rec.lines.vertices);
                transfers.push(rec.lines.colors);
                transfers.push(rec.lines.indices);
            }

            const keys = Array.isArray(m.keys) && m.keys.length > 0 ? m.keys : [ '0/0/0' ];
            const out = [];

            for (const key of keys) {
                out.push({ key: key, fills: rec.fills, lines: rec.lines });
            }

            self.postMessage({ type: 'tiles', z: 0, ok: true, tiles: out }, transfers);
            return;
        }
    } catch (err) {
        self.postMessage({ type: m.type, ok: false, error: String(err.stack || err) });
    }
};

// ---- build a single unit-UV batch from everything ----

function buildUnitBatchesFromAllObjects() {
    const fills = [];
    const lines = [];

    for (const obj of OBJECTS.values()) {
        if (obj.meshes.fills) {
            for (const m of obj.meshes.fills) {
                fills.push(normalizeMesh(m));
            }
        }
        if (obj.meshes.lines) {
            for (const m of obj.meshes.lines) {
                lines.push(normalizeMesh(m));
            }
        }
    }

    const result = {
        fills: fills.length > 0 ? makeBatch(fills) : undefined,
        lines: lines.length > 0 ? makeBatch(lines) : undefined
    };

    return result;
}

function normalizeMesh(m) {
    const W = CONFIG.width > 0 ? CONFIG.width : 1;
    const H = CONFIG.height > 0 ? CONFIG.height : 1;
    const ox = CONFIG.origin && typeof CONFIG.origin.x === 'number' ? CONFIG.origin.x : 0;
    const oy = CONFIG.origin && typeof CONFIG.origin.y === 'number' ? CONFIG.origin.y : 0;

    const src = m.vertices;
    const out = new Float32Array(src.length);

    for (let i = 0; i < src.length; i += 2) {
        out[i] = (src[i] - ox) / W;
        out[i + 1] = (src[i + 1] - oy) / H;
    }

    return {
        vertices: out,
        indices: m.indices,
        color: m.color
    };
}

// ---- meshing (image-space) ----

function toMeshes(fabric, style) {
    const colorFill = style && Array.isArray(style.fill) ? style.fill : [ 0, 0, 0, 0 ];
    const colorLine = style && Array.isArray(style.stroke) ? style.stroke : [ 0, 0, 0, 1 ];
    const widthPx = typeof style?.strokeWidth === 'number' ? style.strokeWidth : 1;

    const outF = [];
    const outL = [];

    if (fabric.type === 'rect') {
        const x = fabric.x;
        const y = fabric.y;
        const w = fabric.w;
        const h = fabric.h;

        if (colorFill[3] > 0) {
            outF.push(triRect(x, y, w, h, colorFill));
        }

        if (colorLine[3] > 0 && widthPx > 0) {
            const loop = [
                { x: x, y: y },
                { x: x + w, y: y },
                { x: x + w, y: y + h },
                { x: x, y: y + h },
                { x: x, y: y }
            ];
            const m = strokeTriangles(loop, widthPx, colorLine);
            if (m) {
                outL.push(m);
            }
        }
    } else if (fabric.type === 'ellipse') {
        const cx = fabric.cx;
        const cy = fabric.cy;
        const rx = fabric.rx;
        const ry = fabric.ry;
        const segments = typeof fabric.segments === 'number' ? fabric.segments : 64;

        const ring = [];
        for (let k = 0; k < segments; k++) {
            const t = (2 * Math.PI * k) / segments;
            ring.push({ x: cx + rx * Math.cos(t), y: cy + ry * Math.sin(t) });
        }

        if (colorFill[3] > 0) {
            const m = triPolygon([ ring ], colorFill);
            if (m) {
                outF.push(m);
            }
        }

        if (colorLine[3] > 0 && widthPx > 0) {
            const m = strokeTriangles(ring.concat([ ring[0] ]), widthPx, colorLine);
            if (m) {
                outL.push(m);
            }
        }
    } else if (fabric.type === 'polygon') {
        const rings = normalizeRings(fabric.points);

        if (colorFill[3] > 0) {
            const m = triPolygon(rings, colorFill);
            if (m) {
                outF.push(m);
            }
        }

        if (colorLine[3] > 0 && widthPx > 0) {
            const closed = rings[0].concat([ rings[0][0] ]);
            const m = strokeTriangles(closed, widthPx, colorLine);
            if (m) {
                outL.push(m);
            }
        }
    } else if (fabric.type === 'polyline') {
        if (colorLine[3] > 0 && widthPx > 0) {
            const m = strokeTriangles(fabric.points, widthPx, colorLine);
            if (m) {
                outL.push(m);
            }
        }
    }

    return { fills: outF, lines: outL };
}

function triRect(x, y, w, h, color) {
    const vertices = new Float32Array([
        x, y,
        x + w, y,
        x + w, y + h,
        x, y + h
    ]);

    const indices = new Uint32Array([ 0, 1, 2, 0, 2, 3 ]);

    return {
        vertices: vertices,
        indices: indices,
        color: color
    };
}

function triPolygon(rings, color) {
    const flat = [];
    const holes = [];

    let len = 0;
    for (let r = 0; r < rings.length; r++) {
        const ring = rings[r];

        if (r > 0) {
            holes.push(len);
        }

        for (const p of ring) {
            flat.push(p.x, p.y);
            len = len + 1;
        }
    }

    const idx = self.earcut ? self.earcut(flat, holes, 2) : [];

    if (!idx || idx.length === 0) {
        return null;
    }

    return {
        vertices: Float32Array.from(flat),
        indices: Uint32Array.from(idx),
        color: color
    };
}

function strokeTriangles(points, widthPx, color) {
    const stroked = strokePoly(points, widthPx);

    if (!stroked.indices || stroked.indices.length === 0) {
        return null;
    }

    return {
        vertices: Float32Array.from(stroked.vertices),
        indices: Uint32Array.from(stroked.indices),
        color: color
    };
}

// Minimal polyline stroker (bevel joins, butt caps). Width is in IMAGE pixels.
function strokePoly(points, widthPx) {
    const half = widthPx * 0.5;

    const verts = [];
    const idx = [];

    let base = 0;

    for (let i = 1; i < points.length; i++) {
        const p0 = points[i - 1];
        const p1 = points[i];

        const dx = p1.x - p0.x;
        const dy = p1.y - p0.y;

        const len = Math.hypot(dx, dy);
        const safeLen = len > 0 ? len : 1;

        const nx = -dy / safeLen;
        const ny = dx / safeLen;

        const v0 = [ p0.x - nx * half, p0.y - ny * half ];
        const v1 = [ p0.x + nx * half, p0.y + ny * half ];
        const v2 = [ p1.x - nx * half, p1.y - ny * half ];
        const v3 = [ p1.x + nx * half, p1.y + ny * half ];

        verts.push(v0[0], v0[1]);
        verts.push(v1[0], v1[1]);
        verts.push(v2[0], v2[1]);
        verts.push(v3[0], v3[1]);

        idx.push(base + 0, base + 1, base + 2);
        idx.push(base + 1, base + 3, base + 2);

        base = base + 4;
    }

    return { vertices: verts, indices: idx };
}

// ---- batching (same layout your renderer expects) ----

function makeBatch(meshes) {
    let vCount = 0;
    let iCount = 0;

    for (const m of meshes) {
        vCount = vCount + (m.vertices.length / 2);
        iCount = iCount + m.indices.length;
    }

    const positions = new Float32Array(vCount * 2);
    const colors = new Uint8Array(vCount * 4);
    const indices = new Uint32Array(iCount);

    let vOfs = 0;
    let iOfs = 0;
    let base = 0;

    for (const m of meshes) {
        positions.set(m.vertices, vOfs * 2);

        const r = clamp255(((m.color && m.color[0]) || 0) * 255);
        const g = clamp255(((m.color && m.color[1]) || 0) * 255);
        const b = clamp255(((m.color && m.color[2]) || 0) * 255);
        const a = clamp255(((m.color && m.color[3]) || 1) * 255);

        const localVerts = m.vertices.length / 2;

        for (let k = 0; k < localVerts; k++) {
            const c = (vOfs + k) * 4;
            colors[c] = r;
            colors[c + 1] = g;
            colors[c + 2] = b;
            colors[c + 3] = a;
        }

        for (let k = 0; k < m.indices.length; k++) {
            indices[iOfs + k] = base + m.indices[k];
        }

        base = base + localVerts;
        vOfs = vOfs + localVerts;
        iOfs = iOfs + m.indices.length;
    }

    return { positions: positions, colors: colors, indices: indices };
}

function clamp255(v) {
    const n = Math.round(v);
    if (n < 0) {
        return 0;
    }
    if (n > 255) {
        return 255;
    }
    return n;
}

// ---- utils ----

function normalizeRings(points) {
    return [ points ];
}

function computeAABB(f) {
    if (f.type === 'rect') {
        return { x: f.x, y: f.y, w: f.w, h: f.h };
    }

    if (f.type === 'ellipse') {
        return { x: f.cx - f.rx, y: f.cy - f.ry, w: 2 * f.rx, h: 2 * f.ry };
    }

    const pts = Array.isArray(f.points) ? f.points : [];

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (const p of pts) {
        if (p.x < minX) {
            minX = p.x;
        }
        if (p.y < minY) {
            minY = p.y;
        }
        if (p.x > maxX) {
            maxX = p.x;
        }
        if (p.y > maxY) {
            maxY = p.y;
        }
    }

    if (!isFinite(minX)) {
        return { x: 0, y: 0, w: 0, h: 0 };
    }

    return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

`;
})(typeof self !== 'undefined' ? self : window);